import type { Swiper } from 'swiper';
import { ProductFilter, ProductSort } from 'types/schema';

export const MAX_PRODUCTS = 50;
export const INITIAL_PAGE_SIZE = 10;
export const MAX_FARM_TO_BIZ = 24;

const TOTAL_RESULTS_KEY_PREFIX = 'homeCarousel:totalResults';

// `querySignature` là chữ ký của câu query (xem queryKey trong HomePage/saga), KHÔNG
// phải section.key: prefetch chạy trước khi widget config về nên chưa biết key.
export const getTotalResultsStorageKey = (region: string, querySignature: string) =>
  `${TOTAL_RESULTS_KEY_PREFIX}:${region}:${querySignature}`;

export const getCachedTotalResults = (storageKey: string): number => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return 0;

    const parsed = Number(raw);

    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  } catch {
    return 0;
  }
};

export const setCachedTotalResults = (storageKey: string, totalResults: number) => {
  try {
    if (totalResults > 0) {
      window.localStorage.setItem(storageKey, String(totalResults));
    }
  } catch {
    // ignore quota / private mode
  }
};

const DAY_PLAN_KEY_PREFIX = 'homeCarousel:dayPlan';

export interface IDayPlan {
  seed: number;
  basePage: number;
  totalPages: number;
  /** Số trang ĐẦY — miền xoay của basePage. Xem calcFullPages. */
  fullPages: number;
}

export const getDayPlanKey = (region: string, lang: string, sectionKey: string) =>
  `${DAY_PLAN_KEY_PREFIX}:${region}:${lang}:${sectionKey}`;

export const getDayPlan = (storageKey: string, seed: number): IDayPlan | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || parsed.seed !== seed) return null;

    if (!Number.isInteger(parsed.basePage) || parsed.basePage < 0) return null;

    if (!Number.isInteger(parsed.totalPages) || parsed.totalPages < 1) return null;

    return parsed as IDayPlan;
  } catch {
    return null;
  }
};

export const setDayPlan = (storageKey: string, plan: IDayPlan) => {
  try {
    if (plan.totalPages < 1 || plan.basePage < 0) return;

    window.localStorage.setItem(storageKey, JSON.stringify(plan));
  } catch {
    // ignore quota / private mode
  }
};

export const getSeededBasePage = (seed: number, totalPages: number) => {
  const pages = Math.max(1, totalPages);

  return seed % pages;
};

export const resolveBasePage = (dayPlan: IDayPlan | null, seed: number, cachedTotalPages: number) => {
  if (dayPlan) return dayPlan.basePage;

  return cachedTotalPages > 0 ? getSeededBasePage(seed, cachedTotalPages) : 0;
};

export const getNextPage = (currentPage: number, totalPages: number) => {
  const pages = Math.max(1, totalPages);

  return (Math.max(0, currentPage) + 1) % pages;
};

export const calcTotalPages = (totalResults: number, pageSize: number = INITIAL_PAGE_SIZE) =>
  Math.max(1, Math.ceil(Math.max(0, totalResults) / pageSize));

/** Số trang đầy — miền xoay của basePage. Phải khớp server/utils/homeCarouselSeed.js. */
export const calcFullPages = (totalResults: number, pageSize: number = INITIAL_PAGE_SIZE) =>
  Math.max(1, Math.floor(Math.max(0, totalResults) / pageSize));

export const trimToFullPages = <T>(items: T[], pageSize: number): T[] => {
  if (!items?.length || pageSize <= 0) return items || [];

  if (items.length <= pageSize) return items;

  const fullCount = Math.floor(items.length / pageSize) * pageSize;

  return fullCount > 0 ? items.slice(0, fullCount) : items;
};

const FIRST_SHOWCASE_HINT_KEY_PREFIX = 'homeCarousel:firstShowcase';

export interface IFirstShowcaseHint {
  key: string;
  sort: ProductSort[];
  filter: ProductFilter;
  id?: string | null;
}

// `prefetchFirstShowcase` (HomePage/saga.ts) bắn TRƯỚC khi widgets về nên không thể
// biết widget đầu tiên là widget nào — nó đoán bằng cấu hình đã ghi lại ở LẦN TẢI
// TRANG GẦN NHẤT. Đoán sai (BE đổi thứ tự widget) thì `sameQuery` tự phát hiện lệch,
// rơi về fetch thật, và `loadShowcaseItems` sẽ ghi đè hint đúng cho lần sau — tự lành,
// cùng triết lý với cache `totalPages` ở trên. Khoá theo region, KHÔNG theo lang: widget
// nào hiển thị là quyết định của BE theo region (`widgetsRedesign(regions: [String])`
// không nhận lang), chỉ text hiển thị mới đổi theo ngôn ngữ.
export const getFirstShowcaseHintKey = (region: string) => `${FIRST_SHOWCASE_HINT_KEY_PREFIX}:${region}`;

export const getCachedFirstShowcaseHint = (storageKey: string): IFirstShowcaseHint | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed.key !== 'string' || !Array.isArray(parsed.sort)) return null;

    return parsed as IFirstShowcaseHint;
  } catch {
    return null;
  }
};

export const setCachedFirstShowcaseHint = (storageKey: string, hint: IFirstShowcaseHint) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(hint));
  } catch {
    // ignore quota / private mode
  }
};

export const LOAD_MORE_PREFETCH_GROUPS = 2;

export const MAX_LOAD_MORE_PROBES = 3;

const END_SNAP_EPSILON_PX = 16;

export const dropDegenerateEndSnap = (swiper: Swiper) => {
  if (swiper.destroyed) return;

  const grid = swiper.snapGrid;
  const last = grid.length - 1;

  if (last <= 1 || grid[last] - grid[last - 1] > END_SNAP_EPSILON_PX) return;

  grid.pop();

  const endSnap = grid[grid.length - 1];

  const overshoot = -swiper.translate - endSnap;

  if (!swiper.animating && overshoot > 0 && overshoot <= END_SNAP_EPSILON_PX) {
    swiper.setTranslate(-endSnap);
  }

  swiper.updateProgress();
  swiper.navigation?.update();
};

/** Skip snap fix while loop is on — loop rebuilds snapGrid with clones. */
export const safeDropDegenerateEndSnap = (swiper: Swiper) => {
  if (swiper.destroyed || swiper.params.loop) return;

  dropDegenerateEndSnap(swiper);
};

/** Slide width for a fixed-visible-count desktop row / clamp-to-track mobile row. */
export const calcCarouselSlideWidth = (
  trackWidth: number,
  isMobile: boolean,
  { visibleSlides, spaceBetween }: { visibleSlides: number; spaceBetween: number },
) => {
  if (trackWidth <= 0) return isMobile ? 250 : 298;

  if (isMobile) {
    return Math.max(200, Math.min(250, trackWidth - 24));
  }

  const gaps = (visibleSlides - 1) * spaceBetween;
  return Math.max(1, Math.floor((trackWidth - gaps) / visibleSlides));
};

const NAV_EDGE_TOLERANCE_PX = 4;

/** Whether prev/next nav buttons should show (non-loop), from swiper edge geometry. */
export const computeCarouselNavVisibility = ({
  swiperRect,
  firstSlideRect,
  lastSlideRect,
  isBeginning,
  isEnd,
}: {
  swiperRect: Pick<DOMRect, 'left' | 'right'>;
  firstSlideRect: Pick<DOMRect, 'left' | 'right'>;
  lastSlideRect: Pick<DOMRect, 'left' | 'right'>;
  isBeginning: boolean;
  isEnd: boolean;
}): { showPrev: boolean; showNext: boolean } => {
  const contentOverflows =
    lastSlideRect.right > swiperRect.right + NAV_EDGE_TOLERANCE_PX ||
    firstSlideRect.left < swiperRect.left - NAV_EDGE_TOLERANCE_PX;

  if (!contentOverflows) return { showPrev: false, showNext: false };

  const atBeginning = isBeginning || firstSlideRect.left >= swiperRect.left - NAV_EDGE_TOLERANCE_PX;
  const atEnd = isEnd || lastSlideRect.right <= swiperRect.right + NAV_EDGE_TOLERANCE_PX;

  return { showPrev: !atBeginning, showNext: !atEnd };
};

/**
 * Slides to move for one non-loop prev/next click. Stops at page marks
 * 0, g, 2g, …, count-g (last page flush, no blank tail). Loop mode doesn't use
 * it: it always moves a full page (see slideByPage).
 */
export const getLoopPageStep = ({
  direction,
  realIndex,
  count,
  pageSize,
}: {
  direction: 'prev' | 'next';
  realIndex: number;
  count: number;
  pageSize: number;
}): number => {
  const g = Math.max(1, Math.min(pageSize, count));
  const lastMark = count - g;
  const marks: number[] = [];
  for (let p = 0; p < lastMark; p += g) marks.push(p);
  marks.push(lastMark);

  if (direction === 'next') {
    const nextMark = marks.find((p) => p > realIndex) ?? count;
    return nextMark - realIndex;
  }

  let prevMark = lastMark - count;
  marks.forEach((p) => {
    if (p < realIndex) prevMark = p;
  });
  return realIndex - prevMark;
};

/**
 * Slides that fit fully in view. Slides align at the content box's left edge, but the right
 * padding still shows them (overflow clips at the padding box), so it counts: 3×160+2×16 =
 * 512px fits a 510px swiper with 16px padding. Swiper's own slidesPerViewDynamic ignores it.
 */
export const calcPageCapacity = ({
  width,
  paddingRight,
  slideSize,
  gap,
}: {
  width: number;
  paddingRight: number;
  slideSize: number;
  gap: number;
}) => Math.max(1, Math.floor((width + paddingRight + gap + 1) / (slideSize + gap)));

/**
 * calcPageCapacity for a live swiper, from widths only (not position/loop state). Median slide
 * size is unchanged by repeated laps / loop re-ordering and ignores a lone wider slide (e.g.
 * the mobile BEST_PRICES banner). 0 while not laid out yet.
 */
export const getPageCapacity = (swiper: Swiper) => {
  const sizes = swiper.slidesSizesGrid;

  if (swiper.destroyed || !swiper.width || !sizes?.length) return 0;

  const slideSize = [...sizes].sort((a, b) => a - b)[Math.floor(sizes.length / 2)];

  if (!slideSize) return 0;

  return calcPageCapacity({
    width: swiper.width,
    paddingRight: parseFloat(window.getComputedStyle(swiper.el).paddingRight) || 0,
    slideSize: slideSize,
    gap: Number(swiper.params.spaceBetween) || 0,
  });
};

/**
 * One page prev/next for any visible count. Page = slides fully visible right now, so it
 * follows each section's layout and breakpoint. Needs slidesPerGroup=1 (slideTo lands on
 * exact slides). `count` = distinct items (list may be repeated to fill the loop).
 *
 * Relies on swiper 11 internals not in its .d.ts (`loopFix`, the `_clientLeft` reflow) —
 * re-check the loop path when upgrading swiper.
 */
export const slideByPage = (swiper: Swiper, direction: 'prev' | 'next', count: number, speed?: number) => {
  if (swiper.destroyed || swiper.animating || count <= 0) return;

  const pageSize = Math.max(1, getPageCapacity(swiper));

  if (!swiper.params.loop) {
    // No repeats without loop: every DOM slide counts (incl. non-product ones like banners).
    const slideCount = swiper.slides.length;
    const index = swiper.activeIndex;
    const lastIndex = Math.max(0, slideCount - pageSize);
    if (direction === 'next' ? index >= lastIndex : index <= 0) return;

    const step = getLoopPageStep({ direction: direction, realIndex: index, count: slideCount, pageSize: pageSize });
    swiper.slideTo(direction === 'next' ? index + step : index - step, speed);
    return;
  }

  // Loop: always a full page — last page's empty slots show the first items (design).
  const step = pageSize;
  const loopSwiper = swiper as Swiper & { loopFix: (o: { direction: 'prev' | 'next' }) => void; _clientLeft?: number };

  loopSwiper.params.loopAdditionalSlides = Math.max(swiper.params.loopAdditionalSlides ?? 0, pageSize);
  loopSwiper.loopFix({ direction: direction });
  // Force reflow (as native slideNext does) so loopFix's instant jump commits before the
  // animated slide — otherwise transitionend may never fire and `animating` sticks.
  loopSwiper._clientLeft = swiper.wrapperEl.clientLeft;
  swiper.slideTo(swiper.activeIndex + (direction === 'next' ? step : -step), speed);
};

/** Split `items` into columns of `rows` items — one column per slide gives a multi-row carousel. */
export const chunkColumns = <T>(items: T[], rows: number): T[][] => {
  const size = Math.max(1, rows);
  const columns: T[][] = [];
  for (let i = 0; i < items.length; i += size) columns.push(items.slice(i, i + size));
  return columns;
};

/**
 * chunkColumns for a looping multi-row carousel: when there is more than `minColumns`
 * columns' worth of items, the last column's empty slots are filled with items from the
 * start — the loop continues there anyway (design: last page = remaining + first items).
 */
export const chunkColumnsFilled = <T>(items: T[], rows: number, minColumns: number): T[][] => {
  const gap = items.length % rows;
  const filled =
    rows > 1 && items.length > rows * minColumns && gap ? [...items, ...items.slice(0, rows - gap)] : items;
  return chunkColumns(filled, rows);
};

/** Stable key for looped slides: first lap keeps the item's own key, repeats get a lap suffix. */
export const loopSlideKey = (key: string | number, slideIndex: number, count: number) =>
  slideIndex < count ? key : `${key}~${Math.floor(slideIndex / count)}`;

/** Repeat `items` until at least `minLength` long, so Swiper loop has enough real slides. */
export const repeatForLoop = <T>(items: T[], minLength: number): T[] => {
  if (!items.length || items.length >= minLength) return items;

  const laps = Math.ceil(minLength / items.length);
  return Array.from({ length: laps }, () => items).flat();
};

export const getProductSlidesPerGroup = (showcaseId: string, width: number) => {
  if (width < 768) {
    return 2;
  }

  if (showcaseId === 'BEST_PRICES') {
    return 4;
  }

  return 5;
};
